<div class="zone-page">
  <header class="zone-hero">
    <div>
      <span>TRANSPORT GEOGRAPHY</span>
      <h1>Location Zones</h1>
      <p>Group service areas and define direct neighbours for transport planning.</p>
    </div>
    <p-button label="New Zone" icon="pi pi-plus" severity="success" (onClick)="openCreate()"></p-button>
  </header>

  <section class="zone-directory">
    <div class="section-heading">
      <div>
        <h2>Zone Directory</h2>
        <p>Country, city, and district coverage is exclusive across active zones.</p>
      </div>
      <span class="count-chip">{{ zones.length }} zones</span>
    </div>

    <div *ngIf="message && !editorVisible" class="message error">{{ message }}</div>

    <p-table [value]="zones" [loading]="loading" [paginator]="zones.length > 20" [rows]="20"
      dataKey="id" styleClass="p-datatable-sm zone-table">
      <ng-template pTemplate="header">
        <tr>
          <th>Zone</th>
          <th>Coverage</th>
          <th>Neighbours</th>
          <th>Representative Location</th>
          <th>Status</th>
          <th class="actions-column">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-zone>
        <tr>
          <td>
            <div class="primary-cell">
              <strong>{{ zone.name }}</strong>
              <span>{{ zone.code }}</span>
            </div>
          </td>
          <td>
            <div class="primary-cell">
              <strong>{{ coverageLabel(zone) }}</strong>
              <span>{{ zone.coverages.length }} geographic rules</span>
            </div>
          </td>
          <td>{{ zone.neighbourZoneIds.length }}</td>
          <td>{{ locationName(zone.representativeLocationId) }}</td>
          <td><p-tag [value]="zone.active ? 'ACTIVE' : 'INACTIVE'" [severity]="zone.active ? 'success' : 'secondary'"></p-tag></td>
          <td>
            <div class="row-actions">
              <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit zone" (onClick)="openEdit(zone)"></p-button>
              <p-button [icon]="zone.active ? 'pi pi-ban' : 'pi pi-check'"
                [text]="true" [severity]="zone.active ? 'danger' : 'success'"
                [pTooltip]="zone.active ? 'Deactivate zone' : 'Activate zone'"
                (onClick)="toggleActive(zone)"></p-button>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="empty-state">
              <i class="pi pi-map"></i>
              <strong>No location zones configured</strong>
              <span>Create the first transport service area.</span>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </section>
</div>

<p-dialog [(visible)]="editorVisible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'min(1120px, 96vw)' }" [breakpoints]="{ '860px': '96vw' }"
  [contentStyle]="{ 'max-height': '78vh', overflow: 'auto' }"
  styleClass="master-create-dialog zone-editor-dialog"
  [header]="editor.id ? 'Edit Location Zone' : 'Create Location Zone'">
  <section class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon"><i class="pi pi-map"></i></div>
      <div>
        <small>TRANSPORT GEOGRAPHY</small>
        <h3>{{ editor.name || 'New location zone' }}</h3>
        <p>Define the explicit service area used to consolidate releases and assign transport capacity.</p>
      </div>
      <div class="master-dialog-facts">
        <div>
          <span>Coverage rules</span>
          <strong>{{ editor.coverages.length }}</strong>
        </div>
        <div>
          <span>Direct neighbours</span>
          <strong>{{ editor.neighbourZoneIds.length }}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{{ editor.active ? 'Active' : 'Inactive' }}</strong>
        </div>
      </div>
    </aside>

    <div class="master-dialog-form">
      <section class="zone-form-section master-wide-field">
        <div class="form-section-heading">
          <div>
            <h3>Zone Details</h3>
            <p>Use a stable code for planning integrations and reporting.</p>
          </div>
          <label class="active-control">
            <p-checkbox [(ngModel)]="editor.active" [binary]="true" inputId="zone-active"></p-checkbox>
            Active
          </label>
        </div>

        <div class="details-grid">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="zone-name" pInputText [(ngModel)]="editor.name" maxlength="150">
              <label for="zone-name">Zone Name *</label>
            </p-iftalabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="zone-code" pInputText [(ngModel)]="editor.code" maxlength="60">
              <label for="zone-code">Zone Code *</label>
            </p-iftalabel>
          </p-inputGroup>

          <p-inputGroup class="span-two">
            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
            <p-iftalabel>
              <input id="zone-description" pInputText [(ngModel)]="editor.description" maxlength="500">
              <label for="zone-description">Description</label>
            </p-iftalabel>
          </p-inputGroup>

          <p-inputGroup class="span-two">
            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
            <p-iftalabel>
              <p-select inputId="representative-location" appLazyLocationPicker
                [(ngModel)]="editor.representativeLocationId" optionLabel="name" optionValue="id"
                [filter]="true" filterBy="name,externalLocationReference" [showClear]="true"
                appendTo="body" styleClass="w-full"></p-select>
              <label for="representative-location">Representative Location</label>
            </p-iftalabel>
          </p-inputGroup>
        </div>
      </section>

      <section class="zone-form-section master-wide-field">
        <div class="form-section-heading">
          <div>
            <h3>Geographic Coverage</h3>
            <p>Each active country, city, or district can belong to one zone only.</p>
          </div>
          <p-button label="Add Coverage" icon="pi pi-plus" severity="secondary"
            [outlined]="true" (onClick)="addCoverage()"></p-button>
        </div>

        <div class="coverage-list">
          <div class="coverage-row" *ngFor="let coverage of editor.coverages; let index = index">
            <div class="coverage-fields">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-filter"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select [inputId]="'coverage-type-' + index" [options]="coverageTypes"
                    [(ngModel)]="coverage.coverageType" optionLabel="label" optionValue="value"
                    (ngModelChange)="onCoverageTypeChanged(coverage)" appendTo="body"
                    styleClass="w-full"></p-select>
                  <label [for]="'coverage-type-' + index">Coverage Type</label>
                </p-iftalabel>
              </p-inputGroup>

              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select [inputId]="'coverage-country-' + index" [options]="countries"
                    [(ngModel)]="coverage.countryId" optionLabel="name" optionValue="id"
                    [filter]="true" filterBy="name,countryCode" appendTo="body"
                    (ngModelChange)="onCoverageCountryChanged(coverage)" styleClass="w-full"></p-select>
                  <label [for]="'coverage-country-' + index">Country *</label>
                </p-iftalabel>
              </p-inputGroup>

              <p-inputGroup *ngIf="coverage.coverageType !== 'COUNTRY'; else cityNotRequired">
                <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select [inputId]="'coverage-city-' + index" [options]="citiesFor(coverage)"
                    [(ngModel)]="coverage.cityId" optionLabel="cityName" optionValue="id"
                    [filter]="true" filterBy="cityName" [disabled]="!coverage.countryId"
                    appendTo="body" styleClass="w-full"></p-select>
                  <label [for]="'coverage-city-' + index">City *</label>
                </p-iftalabel>
              </p-inputGroup>
              <ng-template #cityNotRequired><div class="not-applicable"><i class="pi pi-map"></i> All cities</div></ng-template>

              <p-inputGroup *ngIf="coverage.coverageType === 'DISTRICT'; else districtNotRequired">
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <input [id]="'coverage-district-' + index" pInputText [(ngModel)]="coverage.district">
                  <label [for]="'coverage-district-' + index">District *</label>
                </p-iftalabel>
              </p-inputGroup>
              <ng-template #districtNotRequired><div class="not-applicable"><i class="pi pi-map-marker"></i> All districts</div></ng-template>

              <p-button icon="pi pi-trash" [text]="true" severity="danger"
                pTooltip="Remove coverage" (onClick)="removeCoverage(index)"></p-button>
            </div>
            <span class="coverage-preview">{{ coverageTitle(coverage) }}</span>
          </div>
        </div>
      </section>

      <section class="zone-form-section master-wide-field">
        <div class="form-section-heading">
          <div>
            <h3>Neighbouring Zones</h3>
            <p>Planning can optionally include these zones one hop away.</p>
          </div>
        </div>
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
          <p-iftalabel>
            <p-multiSelect inputId="neighbouring-zones" [options]="neighbourOptions()"
              [(ngModel)]="editor.neighbourZoneIds" optionLabel="name" optionValue="id"
              display="chip" [filter]="true" filterBy="name,code" appendTo="body"
              styleClass="w-full"></p-multiSelect>
            <label for="neighbouring-zones">Direct Neighbouring Zones</label>
          </p-iftalabel>
        </p-inputGroup>
      </section>

      <div *ngIf="message" class="message error master-wide-field">{{ message }}</div>
    </div>
  </section>

  <ng-template pTemplate="footer">
    <div class="master-dialog-footer">
      <p-button label="Reset" icon="pi pi-refresh" severity="secondary"
        [outlined]="true" (onClick)="resetEditor()"></p-button>
      <span class="footer-spacer"></span>
      <p-button label="Cancel" icon="pi pi-times" severity="secondary"
        [outlined]="true" (onClick)="editorVisible = false"></p-button>
      <p-button label="Save Zone" icon="pi pi-save" severity="success"
        [loading]="saving" (onClick)="save()"></p-button>
    </div>
  </ng-template>
</p-dialog>
